import React, { Component } from "react";
import PropTypes from "prop-types";
import getComponentDisplayName from "get-component-display-name";
import AuthActions from "actions/auth-actions";
import sharedActions from "actions/shared-actions";
import {
  ChangePaymentMethodModalNames,
  membershipStatuses,
  billingStatuses,
  shipmentActions,
  membershipTriggeredModalsCMSKeys,
} from "rtr-constants";
import { getMembershipChargeAmount } from "actions/membership-charge-actions";
import { submitStartSwap } from "actions/membership-state-actions";
import MembershipHelpers from "helpers/membership-helpers";
import { isFuturePausedWithoutItemsWithinThreeDays } from "helpers/membership-plan-helpers";
import ManagePaymentMethods from "components/source/shared/manage-payment-methods";
import AddShipmentModal from "components/source/membership/modals/AddShipmentModal/AddShipmentModalWrapper";
import { isAuthorized } from "components/source/hoc/with-user-data";
import { HEAP_AUTH_TRIGGER_TYPES } from "helpers/heap-helpers";

//
// NOTE Using a simple fallback modal for complete failure in pulling
//      information a la internet down, 4XX -, and 5XX errors, etc.
//
//      Allows user to not completely be S.O.L. if something unforeseen
//      happens mid-process, and still gives them a CTA:
//      (i.e., "Call us, Something's wrong")
//
// TODO Establish better default that is not dependent on CMS content.
//      Need more feedback from PM, business.
//
// TODO Establish universal convention for ALL calls in the event of utter
//      failure
//
//
const WTF_ENDPOINT_FAILS_FALLBACK_MODAL = membershipTriggeredModalsCMSKeys.oops;

const withMembershipStateInterface = WrappedComponent => {
  class MembershipStateInterface extends Component {
    membershipState() {
      return this.props.membershipState || {};
    }

    hasFutureCancelDate() {
      const membershipState = this.membershipState();
      return Boolean(
        membershipState.membershipEndedOn &&
          !this.isInactive() &&
          !MembershipHelpers.hasShipmentsCount(membershipState) &&
          this.isShipmentActionNone()
      );
    }

    isShipmentActionNone() {
      return this.membershipState().shipmentAction === shipmentActions.NONE;
    }

    isActiveMember() {
      return this.membershipState().membershipStatus === membershipStatuses.ACTIVE;
    }

    isInactive() {
      return this.membershipState().membershipStatus === membershipStatuses.INACTIVE;
    }

    isInSkipMode() {
      return this.membershipState().billingStatus === billingStatuses.PAUSED;
    }

    isInPaymentRetry() {
      return this.membershipState().billingStatus === billingStatuses.RETRY;
    }

    isInGoodStanding() {
      return this.membershipState().billingStatus === billingStatuses.GOOD_STANDING;
    }

    isFuturePausedWithoutItemsWithinThreeDays() {
      return isFuturePausedWithoutItemsWithinThreeDays(this.membershipState().membershipPausedOn);
    }

    dispatchOnAuthorizedOrPromptLogin(action) {
      if (isAuthorized(this.props.userData)) {
        this.props.dispatch(action);
      } else {
        this.props.dispatch(AuthActions.showAuthModal({ triggeredBy: HEAP_AUTH_TRIGGER_TYPES.AUTH_PROMPT }));
      }
    }

    handleOpenAddShipmentModal() {
      this.dispatchOnAuthorizedOrPromptLogin(sharedActions.displayModal(AddShipmentModal.modalName));
    }

    handleOpenFutureCancelModal() {
      this.dispatchOnAuthorizedOrPromptLogin(
        sharedActions.displayModal(membershipTriggeredModalsCMSKeys.futureCanceled)
      );
    }

    handleOpenFuturePausedModal() {
      this.dispatchOnAuthorizedOrPromptLogin(sharedActions.displayModal(membershipTriggeredModalsCMSKeys.futurePaused));
    }

    handleCancelFuturePausedModal() {
      this.dispatchOnAuthorizedOrPromptLogin(sharedActions.displayModal(membershipTriggeredModalsCMSKeys.futurePaused));
    }

    handleOpenRetryModal() {
      this.dispatchOnAuthorizedOrPromptLogin(
        sharedActions.displayModal(ChangePaymentMethodModalNames.changePaymentMethodFromReturnFlow)
      );
    }

    handleOpenRetryConfirmationModal() {
      this.dispatchOnAuthorizedOrPromptLogin(
        getMembershipChargeAmount(membershipTriggeredModalsCMSKeys.retryConfirmation, WTF_ENDPOINT_FAILS_FALLBACK_MODAL)
      );
    }

    handleOpenPausedConfirmationModal() {
      this.dispatchOnAuthorizedOrPromptLogin(
        getMembershipChargeAmount(
          membershipTriggeredModalsCMSKeys.resumeIntentConfirmation,
          WTF_ENDPOINT_FAILS_FALLBACK_MODAL
        )
      );
    }

    handleGenericDataFailureForModal() {
      this.props.dispatch(sharedActions.displayModal(WTF_ENDPOINT_FAILS_FALLBACK_MODAL));
    }

    handleOpenStartSwapModal() {
      this.props.dispatch(sharedActions.displayModal(membershipTriggeredModalsCMSKeys.startSwap));
    }

    handleSubmitStartSwapAction() {
      this.props.dispatch(submitStartSwap());
    }

    render() {
      return (
        <WrappedComponent
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...this.props}
          membershipState={this.membershipState()}
          hasFutureCancelDate={this.hasFutureCancelDate()}
          isActiveMember={this.isActiveMember()}
          isInactive={this.isInactive()}
          isFuturePausedWithoutItemsWithinThreeDays={this.isFuturePausedWithoutItemsWithinThreeDays()}
          isInGoodStanding={this.isInGoodStanding()}
          isInPaymentRetry={this.isInPaymentRetry()}
          isInSkipMode={this.isInSkipMode()}
          onOpenAddShipmentModal={this.handleOpenAddShipmentModal.bind(this)}
          onOpenFutureCancelModal={this.handleOpenFutureCancelModal.bind(this)}
          onOpenFuturePausedModal={this.handleOpenFuturePausedModal.bind(this)}
          onCancelFuturePausedModal={this.handleCancelFuturePausedModal.bind(this)}
          onOpenRetryModal={this.handleOpenRetryModal.bind(this)}
          onOpenPausedModal={this.handleOpenPausedConfirmationModal.bind(this)}
          onOpenStartSwapModal={this.handleOpenStartSwapModal.bind(this)}
          onSubmitStartSwapAction={this.handleSubmitStartSwapAction.bind(this)}>
          <ManagePaymentMethods
            onCreatePaymentMethodSuccess={this.handleOpenRetryConfirmationModal.bind(this)}
            onUpdatePaymentMethodsSuccess={this.handleOpenRetryConfirmationModal.bind(this)}
            modalName={ChangePaymentMethodModalNames.changePaymentMethodFromReturnFlow}
            showMakeDefaultCheckbox={false}
            setAsDefault={true}
          />
        </WrappedComponent>
      );
    }
  }

  MembershipStateInterface.propTypes = {
    dispatch: PropTypes.func.isRequired,
    membershipState: PropTypes.object.isRequired,
    userData: PropTypes.object.isRequired,
  };

  MembershipStateInterface.displayName = `MembershipStateInterface(${getComponentDisplayName(WrappedComponent)})`;
  return MembershipStateInterface;
};

export default withMembershipStateInterface;
